import { useEffect, useState } from 'react';
import {
  getInstagramInsights,
  getInstagramPosts,
  publishInstagramPost,
  replyToInstagramComment,
  InstagramInsight,
  InstagramPost,
  InstagramProfileStats,
} from '../../../api/instagram.service';

type Screen = 'comments' | 'publish' | 'insights';

function formatInsightValue(value: unknown) {
  if (value === undefined || value === null) return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export default function InstagramTools({
  screen,
  businessIgId,
}: {
  screen: Screen;
  businessIgId: string;
}) {
  if (screen === 'comments') return <CommentsScreen businessIgId={businessIgId} />;
  if (screen === 'publish') return <PublishScreen businessIgId={businessIgId} />;
  return <InsightsScreen businessIgId={businessIgId} />;
}

function PostsList({ businessIgId, refreshKey = 0 }: { businessIgId: string; refreshKey?: number }) {
  const [posts, setPosts] = useState<InstagramPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [replyFor, setReplyFor] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [commentDrafts, setCommentDrafts] = useState<Record<string, string>>({});
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!businessIgId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getInstagramPosts(businessIgId)
      .then((result) => {
        if (cancelled) return;
        if (!result.success) {
          setPosts([]);
          setError(result.error || 'Could not load Instagram posts.');
          return;
        }
        setPosts(result.posts);
      })
      .catch(() => {
        if (cancelled) return;
        setPosts([]);
        setError('Could not load Instagram posts.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [businessIgId, refreshKey, reloadKey]);

  const sendAsBusiness = async (payload: { commentId?: string; mediaId?: string; message: string }) => {
    const message = payload.message.trim();
    if (!message) return;
    setSending(true);
    setError(null);
    try {
      const result = await replyToInstagramComment({
        message,
        commentId: payload.commentId,
        mediaId: payload.mediaId,
        businessIgId,
      });
      if (!result?.success) {
        setError(result?.error || 'Could not post as the business.');
        return;
      }
      setDraft('');
      setReplyFor(null);
      if (payload.mediaId) {
        setCommentDrafts((current) => ({ ...current, [payload.mediaId as string]: '' }));
      }
      setReloadKey((value) => value + 1);
    } catch {
      setError('Could not post as the business.');
    } finally {
      setSending(false);
    }
  };

  if (loading) return <p className="text-sm text-gray-500">Loading posts...</p>;
  if (posts.length === 0 && error) return <p className="text-sm text-red-600">{error}</p>;
  if (posts.length === 0) return <p className="text-sm text-gray-500">No Instagram posts yet.</p>;

  return (
    <div className="space-y-4">
      {error ? <p className="text-sm text-red-600">{error}</p> : null}
      {posts.map((post) => (
        <article key={post.id} className="overflow-hidden rounded-xl border border-gray-100">
          {post.imageUrl ? (
            <img src={post.imageUrl} alt="" className="max-h-80 w-full object-cover" />
          ) : null}
          <div className="p-4">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-semibold text-gray-900">
                {post.likeCount ?? '—'} likes
              </p>
              <p className="text-xs text-gray-500">
                {post.timestamp ? new Date(post.timestamp).toLocaleString() : ''}
              </p>
            </div>
            {post.caption ? <p className="mt-2 text-sm text-gray-800">{post.caption}</p> : null}
            <div className="mt-3 space-y-3 border-t border-gray-100 pt-3">
              {post.comments.map((comment) => (
                <div key={comment.id}>
                  <p className="text-sm text-gray-800">
                    <span className="font-semibold">@{comment.username || 'instagram'}</span> {comment.text}
                  </p>
                  {(comment.replies || []).length > 0 ? (
                    <div className="mt-2 space-y-2 border-l-2 border-gray-200 pl-3">
                      {(comment.replies || []).map((reply) => (
                        <p key={reply.id} className="text-sm text-gray-700">
                          <span className="font-semibold">@{reply.username || 'business'}</span> {reply.text}
                        </p>
                      ))}
                    </div>
                  ) : null}
                  {replyFor === comment.id ? (
                    <form
                      className="mt-2 flex items-end gap-2 pl-3"
                      onSubmit={(event) => {
                        event.preventDefault();
                        sendAsBusiness({ commentId: comment.id, message: draft });
                      }}
                    >
                      <textarea
                        value={draft}
                        onChange={(event) => setDraft(event.target.value)}
                        rows={2}
                        placeholder="Reply as the business"
                        className="flex-1 resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm"
                      />
                      <button
                        type="submit"
                        disabled={sending || !draft.trim()}
                        className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
                      >
                        {sending ? 'Sending...' : 'Reply'}
                      </button>
                    </form>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setReplyFor(comment.id);
                        setDraft('');
                      }}
                      className="mt-1 text-xs font-medium text-gray-500"
                    >
                      Reply
                    </button>
                  )}
                </div>
              ))}
              <form
                className="flex items-end gap-2"
                onSubmit={(event) => {
                  event.preventDefault();
                  sendAsBusiness({ mediaId: post.id, message: commentDrafts[post.id] || '' });
                }}
              >
                <textarea
                  value={commentDrafts[post.id] || ''}
                  onChange={(event) =>
                    setCommentDrafts((current) => ({ ...current, [post.id]: event.target.value }))
                  }
                  rows={2}
                  placeholder="Comment as the business"
                  className="flex-1 resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm"
                />
                <button
                  type="submit"
                  disabled={sending || !(commentDrafts[post.id] || '').trim()}
                  className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
                >
                  {sending ? 'Sending...' : 'Comment'}
                </button>
              </form>
            </div>
            {post.permalink ? (
              <a
                href={post.permalink}
                target="_blank"
                rel="noreferrer"
                className="mt-3 inline-block text-sm font-medium text-blue-600"
              >
                Open on Instagram
              </a>
            ) : null}
          </div>
        </article>
      ))}
    </div>
  );
}

function CommentsScreen({ businessIgId }: { businessIgId: string }) {
  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-1">Instagram Comments</h2>
      <p className="mb-4 text-sm text-gray-500">
        Comments sit under each post. Reply, or add a new comment, as the business account.
      </p>
      <PostsList businessIgId={businessIgId} />
    </div>
  );
}

function PublishScreen({ businessIgId }: { businessIgId: string }) {
  const [imageUrl, setImageUrl] = useState('');
  const [caption, setCaption] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mediaId, setMediaId] = useState('');
  const [postsRefresh, setPostsRefresh] = useState(0);

  const publish = async () => {
    if (!imageUrl.trim()) return;
    setSending(true);
    setError(null);
    setMediaId('');
    try {
      const result = await publishInstagramPost({
        imageUrl: imageUrl.trim(),
        caption: caption.trim(),
        businessIgId: businessIgId || undefined,
      });
      if (!result?.success) {
        setError(result?.error || 'Could not publish this image.');
        return;
      }
      setMediaId(result.mediaId || '');
      setImageUrl('');
      setCaption('');
      setPostsRefresh((value) => value + 1);
    } catch {
      setError('Could not publish this image.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 max-w-2xl">
      <h2 className="text-xl font-semibold text-gray-900 mb-1">Publish to Instagram</h2>
      <p className="mb-4 text-sm text-gray-500">Use a public image URL. The post goes to the connected Instagram account.</p>
      <form
        className="space-y-3"
        onSubmit={(event) => {
          event.preventDefault();
          publish();
        }}
      >
        <label className="block text-sm font-medium text-gray-700">
          Image URL
          <input
            value={imageUrl}
            onChange={(event) => setImageUrl(event.target.value)}
            placeholder="https://..."
            className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
          />
        </label>
        <label className="block text-sm font-medium text-gray-700">
          Caption
          <textarea
            value={caption}
            onChange={(event) => setCaption(event.target.value)}
            rows={3}
            className="mt-1 w-full resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm"
          />
        </label>
        <button
          type="submit"
          disabled={sending || !imageUrl.trim()}
          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {sending ? 'Publishing...' : 'Publish'}
        </button>
      </form>
      {error ? <p className="mt-3 text-sm text-red-600">{error}</p> : null}
      {mediaId ? <p className="mt-3 text-sm text-green-700">Published. Media ID: {mediaId}</p> : null}
      <div className="mt-8">
        <h3 className="mb-3 text-lg font-semibold text-gray-900">Posts and likes</h3>
        <PostsList businessIgId={businessIgId} refreshKey={postsRefresh} />
      </div>
    </div>
  );
}

function InsightsScreen({ businessIgId }: { businessIgId: string }) {
  const [rows, setRows] = useState<InstagramInsight[]>([]);
  const [profile, setProfile] = useState<InstagramProfileStats | null>(null);
  const [reachLast28Days, setReachLast28Days] = useState<number | null>(null);
  const [mediaId, setMediaId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const load = async (nextMediaId = mediaId) => {
    setLoading(true);
    setError(null);
    try {
      const result = await getInstagramInsights({
        businessIgId: businessIgId || undefined,
        mediaId: nextMediaId.trim() || undefined,
      });
      if (!result?.success) {
        setRows([]);
        setProfile(null);
        setReachLast28Days(null);
        setError(result?.error || 'Could not load insights.');
      } else {
        setRows(result.rows);
        setProfile(result.profile);
        setReachLast28Days(result.reachLast28Days);
      }
    } catch {
      setRows([]);
      setProfile(null);
      setReachLast28Days(null);
      setError('Could not load insights.');
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  };

  useEffect(() => {
    if (!businessIgId) return;
    load('');
  }, [businessIgId]);

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-1">Instagram Insights</h2>
      <p className="mb-4 text-sm text-gray-500">
        Insights for the Instagram account assigned to this user: {businessIgId}
      </p>
      <form
        className="mb-4 flex items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          load();
        }}
      >
        <label className="block flex-1 text-sm font-medium text-gray-700">
          Media ID
          <input
            value={mediaId}
            onChange={(event) => setMediaId(event.target.value)}
            placeholder="Leave empty for the account"
            className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
          />
        </label>
        <button
          type="submit"
          disabled={loading}
          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {loading ? 'Loading...' : 'Fetch'}
        </button>
      </form>
      {error ? <p className="mb-3 text-sm text-red-600">{error}</p> : null}
      {profile ? (
        <div className="mb-6">
          <h3 className="mb-3 text-sm font-semibold text-gray-900">All followers and posts</h3>
          <div className="grid gap-3 sm:grid-cols-4">
            <div className="rounded-xl border border-gray-100 p-4">
              <p className="text-sm text-gray-500">Followers</p>
              <p className="mt-1 text-2xl font-semibold text-gray-900">{profile.followersCount ?? '—'}</p>
            </div>
            <div className="rounded-xl border border-gray-100 p-4">
              <p className="text-sm text-gray-500">Following</p>
              <p className="mt-1 text-2xl font-semibold text-gray-900">{profile.followsCount ?? '—'}</p>
            </div>
            <div className="rounded-xl border border-gray-100 p-4">
              <p className="text-sm text-gray-500">Posts</p>
              <p className="mt-1 text-2xl font-semibold text-gray-900">{profile.mediaCount ?? '—'}</p>
            </div>
            <div className="rounded-xl border border-gray-100 p-4">
              <p className="text-sm text-gray-500">Reach, last 28 days</p>
              <p className="mt-1 text-2xl font-semibold text-gray-900">{reachLast28Days ?? '—'}</p>
            </div>
          </div>
        </div>
      ) : null}
      <h3 className="mb-3 text-sm font-semibold text-gray-900">New activity by day</h3>
      {loaded && !loading && rows.length === 0 && !profile && !error ? (
        <p className="text-sm text-gray-500">No insight numbers returned for this account.</p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {rows.map((row) => (
            <div key={row.id || row.name} className="rounded-xl border border-gray-100 p-4">
              <p className="text-sm font-medium text-gray-900">{row.title || row.name}</p>
              <p className="mt-1 text-xs text-gray-500">
                {row.name === 'follower_count' ? 'New followers that day' : 'Unique views that day'}
              </p>
              <div className="mt-3 space-y-1">
                {(row.values || []).map((item) => (
                  <div key={item.end_time || String(item.value)} className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">
                      {item.end_time ? new Date(item.end_time).toLocaleDateString() : row.period}
                    </span>
                    <span className="font-semibold text-gray-900">{formatInsightValue(item.value)}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
